/**
 * La carte « Suites à donner » de la fiche d'une machine : celles qui sont encore OUVERTES (devis à faire, pièce à
 * commander, retour à prévoir…), nées de ses bons validés (lot I1.2).
 *
 * Lecture seule : écarter ou solder une suite se fait depuis l'écran « Suites à traiter » (la file de tout le parc), qu'un lien
 * ouvre ici sur cette machine. Une suite reste ouverte tant qu'on ne l'a pas traitée, et la carte le dit plutôt que de laisser
 * croire qu'un devis est parti.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type SuiteMachine } from '../api/client';
import { useDroitsBons } from '../auth/useDroits';
import { LIBELLE_SUITE, jourFr } from '../domaine/interventions';

export function SuitesMachine({ assetId }: { assetId: string }) {
  const droits = useDroitsBons();
  const [suites, setSuites] = useState<readonly SuiteMachine[] | null>(null);
  const [total, setTotal] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!droits.peutLireBons) return;
    let annule = false;
    setSuites(null);
    api
      .suitesDeLaMachine(assetId)
      .then((r) => {
        if (annule) return;
        setSuites(r.items);
        setTotal(r.total);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Les suites de cette machine n’ont pas pu être lues.');
      });
    return () => {
      annule = true;
    };
  }, [assetId, droits.peutLireBons]);

  if (!droits.peutLireBons) return null;

  return (
    <div className="carte">
      <h2>Suites à donner</h2>
      <div className="carte-corps">
        {erreur && <div className="message erreur">{erreur}</div>}
        {suites === null && !erreur && <p className="aide">Chargement…</p>}
        {suites !== null && suites.length === 0 && <p className="aide">Aucune suite à donner ouverte sur cette machine.</p>}
        {suites !== null && suites.length > 0 && (
          <>
            <ul className="suites-machine">
              {suites.map((s) => (
                <li key={s.id}>
                  <strong>{LIBELLE_SUITE[s.kind]}</strong> — {s.comment}
                  <div className="aide">
                    Bon <Link to={`/interventions?bon=${s.interventionId}`}>{s.interventionNumber}</Link>
                    {s.performedOn ? ` du ${jourFr(s.performedOn)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
            {total > suites.length && (
              <p className="aide">
                {suites.length} suites affichées sur {total}.
              </p>
            )}
            <p className="aide">
              Une suite reste ouverte tant qu’on ne l’a pas traitée : aucun devis ni aucune commande ne part tout seul. On
              les écarte ou on les clôt depuis <Link to={`/suites?machine=${assetId}`}>Suites à traiter</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
